import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTimes, FaCheck, FaEye, FaCalendar, FaUser, FaGlobe, FaMoneyBillWave, FaClock, FaMapMarkerAlt, FaImage, FaFileAlt, FaDownload } from "react-icons/fa";

const RejectedPost = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedAd, setSelectedAd] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/all/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched rejected Ads:", response.data);
        setAds(response.data || []);
      } catch (err) {
        setError("Failed to fetch ads");
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, []);



  // Filter ads with status 'rejected'
  const rejectedAds = ads.filter((ad) => ad.status === "rejected");

  // Handle view details
  const handleViewDetails = (ad) => {
    setSelectedAd(ad);
    setShowDetailsModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedAd(null);
  };

  // Filtered ads based on search
  const filteredAds = rejectedAds.filter(ad => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    // Check adId, reporterId, channelName, or reporter name in proofs
    if (ad.adId && ad.adId.toString().toLowerCase().includes(q)) return true;
    if (ad.proofs && ad.proofs.some(proof =>
      (proof.reporterId && proof.reporterId.toString().toLowerCase().includes(q)) ||
      (proof.channelName && proof.channelName.toLowerCase().includes(q)) ||
      (proof.reporterName && proof.reporterName.toLowerCase().includes(q))
    )) return true;
    return false;
  });

  // Pagination logic
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  const paginatedAds = filteredAds.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-red-600 via-pink-600 to-rose-700 rounded-2xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-2 flex items-center gap-3">
                <FaTimes className="text-red-300" />
                Rejected Advertisements
              </h1>
              <p className="text-red-100 text-lg">
                Review and manage rejected advertisements with detailed rejection reasons
              </p>
              <div className="mt-4 flex items-center gap-4">
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-red-100">Total Rejected Ads</span>
                  <div className="text-2xl font-bold text-white">{filteredAds.length}</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <span className="text-sm text-red-100">This Page</span>
                  <div className="text-2xl font-bold text-white">{paginatedAds.length}</div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/pendingposts")}
              >
                <FaClock className="w-4 h-4" />
                Pending
              </button>
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/approvedposts")}
              >
                <FaCheck className="w-4 h-4" />
                Approved
              </button>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/runningposts")}
              >
                <FaGlobe className="w-4 h-4" />
                Running
              </button>
              <button
                className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                onClick={() => navigate("/admin/completedposts")}
              >
                <FaCheck className="w-4 h-4" />
                Completed
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 bg-gray-50 focus:bg-white"
                placeholder="Search by Ad ID, Reporter ID, Channel Name, or Reporter Name..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-red-50 rounded-lg px-4 py-2">
              <span className="text-sm text-red-600 font-medium">Page {currentPage} of {totalPages}</span>
            </div>
            <div className="bg-green-50 rounded-lg px-4 py-2">
              <span className="text-sm text-green-600 font-medium">{filteredAds.length} Results</span>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              <FaDownload className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-red-500 animate-spin"></div>
              <div className="absolute inset-2 rounded-full border-4 border-transparent border-r-pink-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            </div>
            <p className="text-gray-600 font-medium">Loading rejected ads...</p>
          </div>
        </div>
      ) : paginatedAds.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center">
            <FaTimes className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            {search.trim() ? "No Results Found" : "No Rejected Advertisements"}
          </h3>
          <p className="text-gray-500 text-lg mb-6">
            {search.trim()
              ? "No rejected ads match your search criteria."
              : "You don't have any rejected advertisements at the moment."
            }
          </p>
          {search.trim() && (
            <button
              onClick={() => setSearch("")}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
            >
              Clear Search
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="bg-gradient-to-r from-red-50 to-pink-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-red-500 text-white rounded-lg px-3 py-2">
                <FaTimes className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Rejected Advertisements</h3>
                <p className="text-sm text-gray-600">Showing {paginatedAds.length} of {filteredAds.length} rejected ads</p>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaCalendar className="inline w-4 h-4 mr-2" />
                    Start Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaCalendar className="inline w-4 h-4 mr-2" />
                    End Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Ad ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaUser className="inline w-4 h-4 mr-2" />
                    Target Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaMoneyBillWave className="inline w-4 h-4 mr-2" />
                    Budget
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaClock className="inline w-4 h-4 mr-2" />
                    Ad Length
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaEye className="inline w-4 h-4 mr-2" />
                    Required Views
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <FaUser className="inline w-4 h-4 mr-2" />
                    Required Users
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedAds.map((ad) => (
                  <tr key={ad._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ad.startDate}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ad.endDate}
                    </td>
                    <td className="px-6 py-4">
                      <div className="bg-red-100 text-red-800 rounded-lg px-3 py-1 font-mono text-sm">
                        {ad._id}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${ad.userType === 'influencer'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                        }`}>
                        {ad.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        ₹{ad.totalCost}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ad.adLength}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ad.requiredViews?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ad.requiredReporter}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                        <FaTimes className="w-3 h-3 mr-1" />
                        Rejected
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewDetails(ad)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition-colors shadow-sm hover:shadow-md"
                      >
                        <FaEye className="w-3 h-3" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <nav className="inline-flex items-center space-x-2 bg-white rounded-2xl shadow-lg p-2 border border-gray-100">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                }`}
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === pageNum
                  ? 'bg-red-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                  }`}
                aria-current={currentPage === pageNum ? 'page' : undefined}
              >
                {pageNum}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto relative border border-gray-200">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-red-600 via-pink-600 to-rose-700 text-white p-6 rounded-t-2xl relative">
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-200 text-2xl font-bold bg-white bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                onClick={closeModal}
                title="Close"
              >
                &times;
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <FaEye className="w-6 h-6 text-red-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    Advertisement Details
                  </h3>
                  <p className="text-red-100 text-sm">
                    Complete information about this rejected advertisement
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">

              {/* Information Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Basic Information Card */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-500 text-white rounded-lg p-2">
                      <FaFileAlt className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Basic Information</h5>
                      <p className="text-sm text-gray-600">Advertisement details and type</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad ID</span>
                      <span className="font-mono text-sm text-gray-900 break-all">{selectedAd._id}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad Type</span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                        {selectedAd.adType}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Target User Type</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedAd.userType === 'influencer'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                        }`}>
                        {selectedAd.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Media Type</span>
                      <span className="font-medium text-gray-900">{selectedAd.mediaType}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Ad Length</span>
                      <span className="font-medium text-gray-900">{selectedAd.adLength}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Status</span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                        <FaTimes className="w-3 h-3 mr-1" />
                        Rejected
                      </span>
                    </div>
                  </div>
                </div>

                {/* Campaign Details Card */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-500 text-white rounded-lg p-2">
                      <FaCalendar className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Campaign Details</h5>
                      <p className="text-sm text-gray-600">Timeline and requirements</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Start Date</span>
                      <span className="font-medium text-gray-900">{selectedAd.startDate}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">End Date</span>
                      <span className="font-medium text-gray-900">{selectedAd.endDate}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Views</span>
                      <span className="text-xl font-bold text-green-600">
                        {selectedAd.requiredViews?.toLocaleString()}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Required Users</span>
                      <span className="text-lg font-bold text-green-600">
                        {selectedAd.requiredReporter}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Financial Details Card */}
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-2xl border border-yellow-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-yellow-500 text-white rounded-lg p-2">
                      <FaMoneyBillWave className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Financial Details</h5>
                      <p className="text-sm text-gray-600">Budget and pricing information</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-yellow-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Total Budget</span>
                      <span className="text-2xl font-bold text-yellow-600">
                        ₹{selectedAd.totalCost}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-yellow-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Subtotal</span>
                      <span className="text-lg font-bold text-yellow-600">
                        ₹{selectedAd.subtotal}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-yellow-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">GST</span>
                      <span className="text-lg font-bold text-yellow-600">
                        ₹{selectedAd.gst}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Location Details Card */}
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl border border-indigo-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-indigo-500 text-white rounded-lg p-2">
                      <FaMapMarkerAlt className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Location Details</h5>
                      <p className="text-sm text-gray-600">Geographic targeting information</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-indigo-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">State</span>
                      <span className="font-medium text-gray-900">{selectedAd.adState}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-indigo-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">City</span>
                      <span className="font-medium text-gray-900">{selectedAd.adCity}</span>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-indigo-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">All States</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedAd.allStates ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {selectedAd.allStates ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Rejection Details Card */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-2xl border border-red-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-red-500 text-white rounded-lg p-2">
                      <FaTimes className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Rejection Details</h5>
                      <p className="text-sm text-gray-600">Reason and timing for rejection</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-red-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-2">Rejection Reason</span>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-gray-700">
                        {selectedAd.adminRejectNote || 'No reason provided'}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-red-200">
                      <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Rejected At</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedAd.updatedAt ? new Date(selectedAd.updatedAt).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ad Content */}
              {selectedAd.adContent && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-200 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-gray-500 text-white rounded-lg p-2">
                      <FaFileAlt className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Ad Content</h5>
                      <p className="text-sm text-gray-600">Advertisement text and description</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{selectedAd.adContent}</p>
                  </div>
                </div>
              )}

              {/* Ad Media */}
              {selectedAd.adMedia && (
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-500 text-white rounded-lg p-2">
                      <FaImage className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800">Ad Media</h5>
                      <p className="text-sm text-gray-600">Advertisement image or visual content</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex justify-center">
                      <img
                        src={selectedAd.adMedia}
                        alt="Ad Media"
                        className="max-w-full h-auto max-h-80 rounded-lg shadow-sm border border-gray-200"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/fallback-image.jpg";
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-4">
                <button
                  onClick={closeModal}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RejectedPost;
